import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { GoodsReceiptLine } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { CostViewPermission, Direction, IGoodsReceiptLine } from '../../graphql/schema/p4types';
import {
  useAttributesForWorkspace,
  useLotAttributesForWorkspace,
} from '../../components/Workspace/useAttributesForWorkspace';
import productAttributeQuery from '../../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import lotAttributeQuery from '../../graphql/queries/product/LotAttributeConfigListQuery.graphql';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import ViewContext, { ViewContextProvider } from '../../components/Context/ViewContext';
import {
  useGoodsReceiptLineWorkspaceActions,
  useSupplierReturnFromGoodsReceiptLinesContextAction,
} from '../../actions/product/GoodsReceiptLineWorkspaceAction';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { MenuItem } from 'react-contextmenu';
import AppContext from '../../components/Context/AppContext';
import getProductDP from '../../utils/getProductDP';
import { usePrecision } from '../../utils/hooks';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const GOODS_RECEIPT_LINE_FIELD_NAMES: Array<Paths<IGoodsReceiptLine>> = [
  'product.description',
  'quantity',
  'binLocation.location',
  'goodsReceipt.supplier.name',
  'goodsReceipt.receiptDate',
  'vendorRating.description',
  'product.attributeValues',
];

const GOODS_RECEIPT_LINE_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  'goodsReceipt.receiptDate': {
    name: 'Receipt Date',
    key: 'receiptDate',
    formatter: Formatters.DateFormatter,
    width: 140,
  },
};

const GoodsReceiptLineWorkspaceActions = (props) => {
  const [goodsReceiptLineWorkspaceActions, modals] = useGoodsReceiptLineWorkspaceActions();

  const viewContext = useMemo(
    () => ({
      uniqueName: 'GoodsReceiptLineWorkspaceActions',
    }),
    []
  );

  // View Context wrapper is to 'hide' the workspace view context from the form.
  // Otherwise we get some unintentional side effects (e.g. Shimmer rows on the item grid).
  return (
    <>
      <ViewContextProvider value={viewContext}>{modals}</ViewContextProvider>
      <WorkspaceActionButtons {...props} actionButtons={goodsReceiptLineWorkspaceActions} />
    </>
  );
};

const GoodsReceiptLineWorkspace = (props) => {
  const appContext = useContext(AppContext);
  const costPricePrecision = usePrecision('cost');

  const columnOverrides: IWorkspaceColumnOverride = useMemo(() => {
    return {
      ...GOODS_RECEIPT_LINE_COLUMN_OVERRIDES,
      costPrice: {
        options: { format: { decimals: costPricePrecision } },
      },
    };
  }, [costPricePrecision]);

  const productAttributeColumns = useAttributesForWorkspace(productAttributeQuery, {
    valuesPath: 'product.attributeValues',
    columnKey: 'product.attributeValues.attributeConfig.title',
  });

  const lotAttributeColumns = useLotAttributesForWorkspace(lotAttributeQuery, {
    valuesPath: 'stockLot.lotAttributeValues',
    columnKey: 'stockLot.lotAttributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(() => {
    return { ...productAttributeColumns, ...lotAttributeColumns };
  }, [productAttributeColumns, lotAttributeColumns]);

  const workspaceProps = useWorkspace<IGoodsReceiptLine>(props, GoodsReceiptLine, {
    columns: props.defaultColumns || GOODS_RECEIPT_LINE_FIELD_NAMES,
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: props.initialSortColumn || 'product.description',
    initialSortDirection: props.initialSortDir || Direction.Asc,
  });

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef) => {
      // Exclude invalid lot attribute columns e.g. 'lot attributes - text value', 'attribute config - title' etc.
      if (
        !column?.fieldName ||
        column.fieldName.includes('pricePotentialList.') ||
        (column.fieldName.includes('stockLot.lotAttributeValues') &&
          !column.fieldName.includes('stockLot.lotAttributeValues.attributeConfig.title.'))
      ) {
        return false;
      }
      return !(
        appContext?.fullUserDetails?.companyUser?.costViewPermission !== CostViewPermission.All &&
        (column.fieldName === 'costPrice' || column.fieldName === 'purchaseOrderLine.assignmentTotal')
      );
    },
    [appContext?.fullUserDetails?.companyUser?.costViewPermission]
  );

  const [raiseReturnAction, supplierReturnModal] = useSupplierReturnFromGoodsReceiptLinesContextAction();

  const getLineContextButtons = ({ rowData }) => {
    const renderedButtons: Array<Array<ReactElement>> = [];

    renderedButtons.push([
      <MenuItem
        key={'raiseReturn'}
        onClick={(e) => {
          e.preventDefault();
          raiseReturnAction([props.filters], [rowData], true);
        }}
      >
        Raise Return
      </MenuItem>,
    ]);

    return renderedButtons;
  };

  const outerViewContext = useContext(ViewContext);

  return (
    <>
      <Workspace
        entity={GoodsReceiptLine}
        entityType={GoodsReceiptLine.type}
        title={'Goods In Report'}
        defaultQuery={GoodsReceiptLine.workspace}
        availableColumnsFilter={availableColumnsFilter}
        workspaceActionButtonsHOC={GoodsReceiptLineWorkspaceActions}
        renderActionButtons={getLineContextButtons}
        {...workspaceProps}
        {...props}
      />
      <ViewContextProvider value={{ viewScreenName: outerViewContext.viewScreenName }}>
        {supplierReturnModal}
      </ViewContextProvider>
    </>
  );
};

export default GoodsReceiptLineWorkspace;
